import * as Sentry from "@sentry/nuxt";
import { useAuthStore } from "~/stores/auth";
import type { ApiInstance } from "./01.api";
import type { Auth, Site } from "@/types/plugins";
import { apiExceptionStatus } from "~/utils/apiException";

export default defineNuxtPlugin(async (nuxtApp) => {
  addRouteMiddleware((to, from) => {
    if (
      !isLoggedIn &&
      !to.path.includes("/customer/login") &&
      !to.path.includes("/api")
    ) {
      // const cookie = useCookie("auth_redirect");
      // cookie.value = from.fullPath;

      return navigateTo("/customer/login");
    }
  });

  // Login with a password
  const loginWithPassword = async (email: string, password: string) => {
    const AuthStore = useAuthStore();

    const $api = nuxtApp.$api as ApiInstance;
    const $site = nuxtApp.$site as Site;

    const response: any = await $api.auth.login($site.location, email, password);

    AuthStore.token = response?.token;

    await info();

    return response;
  };

  // Login from authenticated link
  const loginWithLink = async (
    email: string,
    code: string,
    auth: any,
    expires: any
  ) => {
    const AuthStore = useAuthStore();

    const $api = nuxtApp.$api as ApiInstance;
    const $site = nuxtApp.$site as Site;

    const response: any = await $api.auth.loginEmailAuth(
      $site.location,
      email,
      code,
      auth,
      expires
    );

    if (response?.token) {

      AuthStore.token = response?.token;

      await info();

      return response;
    } else {
      throw response;
    }
  };

  // Login in with a code
  const loginWithCode = async (
    customer: any,
    email: string,
    token: string,
    code: string
  ) => {
    const AuthStore = useAuthStore();

    const $api = nuxtApp.$api as ApiInstance;
    const $site = nuxtApp.$site as Site;

    const response: any = await $api.auth.loginCodeAuth(
      $site.location,
      customer,
      email,
      token,
      code
    );

    if (response.token) {
      AuthStore.token = response?.token;

      await info();
    }

    return response;
  };

  const requestLoginCode = async (customer: any, email: string) => {
    const $api = nuxtApp.$api as ApiInstance;
    const $site = nuxtApp.$site as Site;

    const response = await $api.auth.loginCodeRequest(
      $site.location,
      customer,
      email
    );

    return response;
  };

  const setToken = async (token: string) => {
    const AuthStore = useAuthStore();

    AuthStore.token = token;

    await info();

    return true;
  };

  const logout = async () => {
    const AuthStore = useAuthStore();

    AuthStore.token = null;
    AuthStore.user = null;

    return true;
  };

  const info = async () => {
    const AuthStore = useAuthStore();
    const $api = nuxtApp.$api as ApiInstance;

    const response: any = await $api.auth.user();

    if (response?.success) {
      AuthStore.user = response.customer;
    }

    return response;
  };

  const isLoggedIn = computed(() => {
    const AuthStore = useAuthStore();

    return AuthStore.token !== null //  && AuthStore.user !== null;
  });

  const user = computed(() => {
    const AuthStore = useAuthStore();

    return AuthStore.user;
  });

  const init = async () => {
    if (!isLoggedIn.value) return;

    try {
      await info();
    } catch (error: any) {
      const status = apiExceptionStatus(error);

      if (status === 401) {
        await logout();
        return;
      }

      Sentry.captureException(error, {
        tags: {
          auth_bootstrap: "failed",
          status: String(status || "unknown"),
        },
      });
    }
  };

  if (import.meta.server || !user.value) {
    await init();
  }

  const auth: Auth = {
    loginWithPassword,
    loginWithLink,
    loginWithCode,
    requestLoginCode,
    setToken,
    logout,
    info,
    isLoggedIn,
    user,
  };

  nuxtApp.provide("auth", auth);
});
